import { useState, useEffect, useRef } from 'react';

interface UseUploadProgressOptions {
  isLoading: boolean;
  isSuccess: boolean;
  isError: boolean;
  fileSize?: number;
}

export const useUploadProgress = ({
  isLoading,
  isSuccess,
  isError,
  fileSize = 1024 * 1024,
}: UseUploadProgressOptions) => {
  const [progress, setProgress] = useState<number>(0);
  const [isUploading, setIsUploading] = useState<boolean>(false);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);

  // When loading turns true, begin progress tracking
  useEffect(() => {
    if (isLoading && isUploading) {
      setProgress((prev) => (prev === 0 ? 8 : prev));

      // Calculate cadence based on file size:
      // Smaller files (<2MB): 200ms per tick
      // Medium files (2MB-10MB): 350ms per tick
      // Large files (>10MB): 500ms per tick
      const stepDuration = fileSize > 10 * 1024 * 1024 ? 500 : fileSize > 2 * 1024 * 1024 ? 350 : 200;

      if (intervalRef.current) clearInterval(intervalRef.current);

      intervalRef.current = setInterval(() => {
        setProgress((prev) => {
          if (prev >= 95) return 95; // Hold at 95% until server returns
          const remaining = 96 - prev;
          // Smooth asymptotic increment
          const increment = Math.max(1, Math.min(5, Math.floor(remaining * 0.12)));
          return prev + increment;
        });
      }, stepDuration);
    } else if (!isLoading && !isSuccess) {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    }

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [isLoading, isUploading, fileSize]);

  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  // When success fires, jump directly to 100%
  useEffect(() => {
    if (isSuccess && isUploading) {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
      setProgress(100);

      if (timeoutRef.current) clearTimeout(timeoutRef.current);
      timeoutRef.current = setTimeout(() => {
        setIsUploading(false);
        setProgress(0);
      }, 700);

      return () => {
        if (timeoutRef.current) clearTimeout(timeoutRef.current);
      };
    }
  }, [isSuccess, isUploading]);

  // When error fires, immediately reset
  useEffect(() => {
    if (isError && isUploading) {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
      setIsUploading(false);
      setProgress(0);
    }
  }, [isError, isUploading]);

  const startUpload = () => {
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    setIsUploading(true);
    setProgress(5);
  };

  const resetUpload = () => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    setIsUploading(false);
    setProgress(0);
  };

  return { progress, isUploading, startUpload, resetUpload };
};

export default useUploadProgress;
